import React, { useState, useRef, useImperativeHandle, forwardRef } from 'react';
import axios from 'axios';
import { fullStateName } from '../utils/stateNames';

// Empty decomposed-address shape. USPS field names so the value maps straight onto the
// /api/validateAddress request and the standardized response.
export const emptyAddress = {
  streetAddress: '',
  secondaryAddress: '',
  city: '',
  state: '',
  ZIPCode: '',
  ZIPPlus4: '',
};

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN',
  'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH',
  'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT',
  'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'AS', 'GU', 'MP', 'PR', 'VI',
];

// Reduce any ZIP (5-digit or ZIP+4 like "94611-3934") to just the 5-digit code ("94611").
export function zip5(zip) {
  return (zip || '').toString().split('-')[0].trim().slice(0, 5);
}

// Split any ZIP value into its 5-digit code and optional 4-digit add-on, ignoring formatting
// ("12345-6789", "123456789" and "12345 6789" all yield { zip5: '12345', plus4: '6789' }).
export function splitZip(zip) {
  const digits = (zip || '').toString().replace(/\D/g, '').slice(0, 9);
  return { zip5: digits.slice(0, 5), plus4: digits.slice(5) };
}

// Combine a 5-digit code and optional 4-digit add-on into "12345" or "12345-6789".
export function combineZip(five, plus4) {
  const a = (five || '').toString();
  const b = (plus4 || '').toString();
  return b ? `${a}-${b}` : a;
}

// The combined ZIP for a decomposed address, normalized from its ZIPCode field.
export function combinedZip(addr) {
  const { zip5: five, plus4 } = splitZip((addr || {}).ZIPCode);
  return combineZip(five, plus4);
}

// Build the single-line, USPS-standardized string for display fallbacks:
// "<street[ apt]>, <city>, <STATE> <ZIP[-Plus4]>, USA".
export function composeAddress(addr) {
  if (!addr || !addr.streetAddress) return '';
  const street = [addr.streetAddress, addr.secondaryAddress].filter(Boolean).join(' ').trim();
  return `${street}, ${addr.city}, ${addr.state} ${combinedZip(addr)}, USA`;
}

// Map the decomposed address value onto the QuickBase subfield names for a prefix
// ('home' | 'shipping'). These subfields are the source of truth; the backend rolls them up
// into the compiled home_address/shipping_address fields on save. The postal code is stored as
// the combined ZIP ("12345" or "12345-6789" when a ZIP+4 add-on is present).
export function addressToSubfields(addr, prefix) {
  const a = addr || emptyAddress;
  const filled = a.streetAddress || a.city;
  return {
    [`${prefix}_address_street_1`]: a.streetAddress || '',
    [`${prefix}_address_street_2`]: a.secondaryAddress || '',
    [`${prefix}_address_city`]: a.city || '',
    [`${prefix}_address_state_region`]: fullStateName(a.state),
    [`${prefix}_address_postal_code`]: combinedZip(a),
    [`${prefix}_address_country`]: filled ? 'USA' : '',
  };
}

const inputClass =
  'mt-1 block w-full rounded-md border-primary focus:border-primary focus:ring-primary bg-white shadow-sm sm:text-sm';
const disabledInputClass =
  'mt-1 block w-full rounded-md border-transparent bg-gray-50 shadow-sm sm:text-sm';

// Human-readable label for the provider that confirmed the address (from the /validateAddress
// response). USPS is primary; Google/US Census are the fallbacks used when USPS is rate-limited.
const PROVIDER_LABELS = { usps: 'USPS', google: 'Google', census: 'US Census' };

/**
 * Decomposed, USPS-verified address entry. Fully controlled: the parent owns `value`
 * (an emptyAddress-shaped object) and `verified`. Any edit clears `verified`. There is no
 * verify button — the parent triggers verification imperatively via a ref (`ref.current.verify()`)
 * when the DJ saves; `verify()` resolves `true` when the address is confirmed and needs no
 * further action, or `false` when a correction suggestion / error is now shown inline.
 *
 * Soft/partial matches (USPS found the street but couldn't confirm the unit) are confirmation-gated:
 * `verify()` returns `false` on the FIRST encounter and shows an amber warning with a "Save anyway"
 * affordance. Only after the DJ confirms (which re-invokes the parent's save via `onConfirmSave`)
 * does `verify()` return the address so the save proceeds.
 */
const AddressFields = forwardRef(function AddressFields({
  label,
  value,
  onChange,
  disabled = false,
  required = false,
  verified = false,
  onVerifiedChange = () => {},
  onConfirmSave = () => {},
}, ref) {
  const [status, setStatus] = useState('idle'); // idle | verifying | suggestion | warning | error
  const [suggestion, setSuggestion] = useState(null); // { standardized, combined }
  const [message, setMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState(null); // { streetAddress?, city?, state?, ... }
  const [provider, setProvider] = useState(null); // which provider confirmed it (for the badge)
  const [awaitingConfirm, setAwaitingConfirm] = useState(false); // partial match awaiting explicit confirm

  const addr = value || emptyAddress;

  // Whether the DJ has explicitly confirmed a soft/partial match. A ref (not state) so the
  // re-invoked save reads the latest value synchronously, before the confirming re-render commits.
  const partialConfirmedRef = useRef(false);

  // Clear any prior verification/feedback after an edit — shared by every field's onChange.
  const invalidateVerification = () => {
    if (verified) onVerifiedChange(false);
    setStatus('idle');
    setSuggestion(null);
    setMessage('');
    setFieldErrors(null);
    setProvider(null);
    setAwaitingConfirm(false);
    partialConfirmedRef.current = false;
  };

  const setField = (field) => (e) => {
    onChange({ ...addr, [field]: e.target.value });
    invalidateVerification();
  };

  // ZIP input masking: keep at most 9 digits, auto-inserting the "-" after the 5th so the field
  // shows "12345" while short and "12345-6789" once a ZIP+4 add-on is entered.
  const setZip = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 9);
    const formatted = digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
    onChange({ ...addr, ZIPCode: formatted, ZIPPlus4: digits.slice(5) });
    invalidateVerification();
  };

  // Merge a provider's standardized address in, surfacing the combined ZIP+4 in the (single)
  // ZIP input so the DJ sees the full standardized ZIP.
  const mergeStandardized = (base, standardized) => {
    const merged = { ...base, ...standardized };
    if (standardized.ZIPPlus4) {
      merged.ZIPCode = combineZip(standardized.ZIPCode, standardized.ZIPPlus4);
    }
    // Providers return the 2-letter state; keep the address object on the full name so both the
    // dropdown (its option values are full names) and any downstream save carry it.
    merged.state = fullStateName(merged.state);
    return merged;
  };

  const canVerify =
    addr.streetAddress.trim() && addr.city.trim() && addr.state.trim() && addr.ZIPCode.trim();

  // Verify with the address provider chain (USPS, or a Google/Census fallback). Returns the
  // standardized (canonical) address when confirmed with no further action needed; false when a
  // suggestion/error is now displayed (caller should halt the save). The caller persists the
  // returned address, so QuickBase always stores the canonical form.
  const runVerify = async () => {
    if (verified) return addr;
    if (!canVerify) {
      setStatus('error');
      setMessage('Please complete the street, city, state and ZIP fields.');
      return false;
    }

    setStatus('verifying');
    setSuggestion(null);
    setMessage('');
    setFieldErrors(null);
    setAwaitingConfirm(false);
    try {
      const { zip5: zc, plus4 } = splitZip(addr.ZIPCode);
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/validateAddress`,
        {
          streetAddress: addr.streetAddress,
          secondaryAddress: addr.secondaryAddress,
          city: addr.city,
          state: addr.state,
          ZIPCode: zc,
          ZIPPlus4: plus4,
        }
      );

      // Remember which provider answered so the badge can credit it (USPS / Google / US Census).
      setProvider(data.provider || null);

      // Soft/partial match: USPS found the street address but couldn't confirm the secondary unit
      // (DPV 'D'/'S' + correction code 32). The address is legitimate, so we surface a non-blocking
      // warning. It is confirmation-gated: the first encounter halts the save and shows a "Save
      // anyway" affordance; only once the DJ confirms does verify() return the address so the save
      // proceeds. Must be checked BEFORE the hard `!data.verified` block below.
      if (data.partial) {
        const standardizedAddr = data.standardized ? mergeStandardized(addr, data.standardized) : addr;
        // USPS may not echo the unit back; keep what the DJ entered so their unit isn't dropped.
        if (!standardizedAddr.secondaryAddress && addr.secondaryAddress) {
          standardizedAddr.secondaryAddress = addr.secondaryAddress;
        }
        onChange(standardizedAddr);
        setStatus('warning');
        setMessage(
          data.dpv === 'S'
            ? `We confirmed the street address but couldn’t verify unit “${addr.secondaryAddress}”. You can save it as entered, or double-check the unit.`
            : 'We confirmed the street address but couldn’t confirm a unit. You can save it as entered, or add/adjust the unit number.'
        );
        if (partialConfirmedRef.current) {
          setAwaitingConfirm(false);
          return standardizedAddr;
        }
        setAwaitingConfirm(true);
        return false;
      }

      if (!data.verified) {
        onVerifiedChange(false);
        setStatus('error');
        setFieldErrors(data.fieldErrors || null);
        setMessage(data.message || 'We could not verify this address. Please check it and try again.');
        return false;
      }

      if (data.changed && data.standardized) {
        // Deliverable, but the provider standardized it — let the DJ accept or keep their version.
        setSuggestion({ standardized: data.standardized, combined: data.combined });
        setStatus('suggestion');
        return false;
      }

      // Verified. Adopt the standardized (canonical) form so the field, the badge, and what we
      // persist all match — even when the only differences are casing/ZIP+4, which the
      // case-insensitive `changed` check treats as unchanged (e.g. "212 RaMona Ave" -> "212 RAMONA AVE").
      const standardizedAddr = data.standardized ? mergeStandardized(addr, data.standardized) : addr;
      onChange(standardizedAddr);
      onVerifiedChange(true);
      setStatus('idle');
      return standardizedAddr;
    } catch (error) {
      onVerifiedChange(false);
      setStatus('error');
      setMessage('We couldn’t reach the address checker. Please try again in a moment.');
      return false;
    }
  };

  // Expose verify() so the parent's Save/Submit handler can trigger verification.
  useImperativeHandle(ref, () => ({ verify: runVerify }));

  const acceptSuggestion = () => {
    onChange(mergeStandardized(addr, suggestion.standardized));
    onVerifiedChange(true);
    setSuggestion(null);
    setStatus('idle');
  };

  const keepMine = () => {
    // The provider confirmed the address is deliverable; keeping the DJ's formatting is fine.
    onVerifiedChange(true);
    setSuggestion(null);
    setStatus('idle');
  };

  // Explicit confirm of a soft/partial match: mark it confirmed (via the ref so the re-invoked
  // save's verify() sees it immediately) and re-trigger the parent's save, which now proceeds.
  const confirmPartial = () => {
    partialConfirmedRef.current = true;
    setAwaitingConfirm(false);
    onConfirmSave();
  };

  // Dismiss the warning so the DJ can adjust the unit; any field edit re-invalidates verification.
  const editPartial = () => {
    partialConfirmedRef.current = false;
    setAwaitingConfirm(false);
    setStatus('idle');
    setMessage('');
  };

  const fieldErrorText = (name) =>
    fieldErrors && fieldErrors[name] ? (
      <p className="mt-1 text-sm text-red-600">{fieldErrors[name]}</p>
    ) : null;

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-1">
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500"> *</span>}
        </label>
        {verified && (
          <span className="inline-flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
            {provider ? `Verified by ${PROVIDER_LABELS[provider] || provider}` : 'Verified'}
          </span>
        )}
      </div>

      <div className="space-y-2">
        <div>
          <input
            type="text"
            aria-label={`${label} street address`}
            placeholder="Street address"
            value={addr.streetAddress}
            onChange={setField('streetAddress')}
            disabled={disabled}
            required={required}
            autoComplete="off"
            className={disabled ? disabledInputClass : inputClass}
          />
          {fieldErrorText('streetAddress')}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div>
            <input
              type="text"
              aria-label={`${label} apartment or suite`}
              placeholder="Apt / Suite (optional)"
              value={addr.secondaryAddress}
              onChange={setField('secondaryAddress')}
              disabled={disabled}
              autoComplete="off"
              className={disabled ? disabledInputClass : inputClass}
            />
            {fieldErrorText('secondaryAddress')}
          </div>
          <div>
            <input
              type="text"
              aria-label={`${label} city`}
              placeholder="City"
              value={addr.city}
              onChange={setField('city')}
              disabled={disabled}
              required={required}
              autoComplete="off"
              className={disabled ? disabledInputClass : inputClass}
            />
            {fieldErrorText('city')}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <div>
            <select
              aria-label={`${label} state`}
              value={addr.state}
              onChange={setField('state')}
              disabled={disabled}
              required={required}
              className={disabled ? disabledInputClass : inputClass}
            >
              <option value="">State</option>
              {US_STATES.map((s) => (
                <option key={s} value={fullStateName(s)}>
                  {s}
                </option>
              ))}
            </select>
            {fieldErrorText('state')}
          </div>
          <div className="col-span-2">
            <input
              type="text"
              aria-label={`${label} ZIP code`}
              placeholder="ZIP code"
              value={addr.ZIPCode}
              onChange={setZip}
              disabled={disabled}
              required={required}
              inputMode="numeric"
              maxLength={10}
              autoComplete="off"
              className={disabled ? disabledInputClass : inputClass}
            />
            {fieldErrorText('ZIPCode')}
          </div>
        </div>
      </div>

      {/* Verification feedback (edit mode only) — verification itself is triggered on save */}
      {!disabled && (
        <div className="mt-3">
          {status === 'verifying' && (
            <p className="flex items-center gap-1.5 text-sm text-gray-500">
              <svg className="h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Verifying address
            </p>
          )}

          {!verified && status !== 'suggestion' && status !== 'verifying' && status !== 'warning' && (
            <p className="text-xs text-gray-500">Your address is validated when you save.</p>
          )}

          {status === 'warning' && (
            <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-3">
              <p className="flex items-start gap-1.5 text-sm text-amber-700">
                <svg className="mt-0.5 h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <span>{message}</span>
              </p>
              {awaitingConfirm && (
                <div className="mt-3 flex flex-col sm:flex-row gap-2">
                  <button
                    type="button"
                    onClick={confirmPartial}
                    className="inline-flex justify-center rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-600"
                  >
                    Save anyway
                  </button>
                  <button
                    type="button"
                    onClick={editPartial}
                    className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  >
                    Edit address
                  </button>
                </div>
              )}
            </div>
          )}

          {status === 'error' && (
            <p className="mt-2 flex items-start gap-1.5 text-sm text-red-600">
              <svg className="mt-0.5 h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <span>{message}</span>
            </p>
          )}

          {status === 'suggestion' && suggestion && (
            <div className="mt-2 rounded-md border border-blue-200 bg-blue-50 p-3">
              <p className="text-sm font-medium text-gray-900">
                {provider
                  ? `${PROVIDER_LABELS[provider] || provider} suggests a standardized address:`
                  : 'Suggested standardized address:'}
              </p>
              <p className="mt-1 text-sm text-gray-700">{suggestion.combined}</p>
              <div className="mt-3 flex flex-col sm:flex-row gap-2">
                <button
                  type="button"
                  onClick={acceptSuggestion}
                  className="inline-flex justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  Use this address
                </button>
                <button
                  type="button"
                  onClick={keepMine}
                  className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  Keep mine
                </button>
              </div>
              <p className="mt-2 text-xs text-gray-500">After choosing, click Save again to finish.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

export default AddressFields;
